import { useSearchParams } from "react-router-dom";
import type { PlaceFeature, PlaceType } from "~/lib/api";
import type { AccentScheme } from "~/lib/placeAccents";
import { cn } from "~/lib/utils";
import { PlaceSectionHeader } from "./PlaceSectionHeader";
import { PlaceListItem } from "./PlaceListItem";
import { FeaturedCard } from "./FeaturedCard";
import { CompactListItem } from "./CompactListItem";
import { getPlaceId } from "./types";

const MAX_FEATURED = 3;

interface PlaceSectionProps {
  type: PlaceType;
  places: PlaceFeature[];
  selectedId: string | null;
  accent: AccentScheme;
  description?: string;
}

export function PlaceSection({ type, places, selectedId, accent, description }: PlaceSectionProps) {
  const [searchParams] = useSearchParams();
  const withImages = places.filter((p) => p.properties.images.length > 0);
  const withoutImages = places.filter((p) => p.properties.images.length === 0);

  const featured = withImages.slice(0, MAX_FEATURED);
  const overflow = withImages.slice(MAX_FEATURED);
  const rest = [...overflow, ...withoutImages];

  const thirdFeatured = featured[2];
  const thirdFeaturedId = thirdFeatured ? getPlaceId(thirdFeatured) : null;
  const hasCompactItems = rest.length > 0 || thirdFeatured;

  const gridCols = {
    "grid-cols-1": featured.length === 1,
    "grid-cols-2": featured.length === 2,
    "grid-cols-2 @lg:grid-cols-3": featured.length >= 3,
  };

  function placeHref(id: string) {
    const params = new URLSearchParams(searchParams);
    params.set("place", id);
    return `?${params.toString()}`;
  }

  return (
    <section>
      <PlaceSectionHeader
        type={type}
        count={places.length}
        accent={accent}
        description={description}
        className="mb-6"
      />

      {featured.length > 0 && (
        <div className={cn("grid gap-3", gridCols)}>
          {featured.map((place, index) => {
            const id = getPlaceId(place);
            return (
              <PlaceListItem key={id} to={placeHref(id)} className="rounded-2xl">
                <FeaturedCard
                  place={place}
                  isSelected={selectedId === id}
                  className={cn({ "hidden @lg:block": index === 2 })}
                />
              </PlaceListItem>
            );
          })}
        </div>
      )}

      {hasCompactItems && (
        <div className="mt-4 space-y-2">
          {thirdFeatured && (
            <PlaceListItem key={`compact-${thirdFeaturedId}`} to={placeHref(thirdFeaturedId!)} className="rounded-xl">
              <CompactListItem
                place={thirdFeatured}
                isSelected={selectedId === thirdFeaturedId}
                accent={accent}
                className="@lg:hidden"
              />
            </PlaceListItem>
          )}
          {rest.map((place) => {
            const id = getPlaceId(place);
            return (
              <PlaceListItem key={id} to={placeHref(id)} className="rounded-xl">
                <CompactListItem place={place} isSelected={selectedId === id} accent={accent} />
              </PlaceListItem>
            );
          })}
        </div>
      )}
    </section>
  );
}
